/**
 * Contract-derived JSDoc typedefs for the Provision card (Story 56425).
 *
 * These mirror `contracts/provision-card.contract.yaml` field-for-field. This project is plain
 * JS/JSX (no TypeScript) — matching the Professional Contacts precedent
 * (src/domain/professionalContacts/professionalContacts.types.js), these typedefs are the
 * hand-derived JS equivalent of what `openapi-typescript` would generate, kept purely for editor
 * hints. Never add a field here that isn't in the contract — if the UI needs something the
 * contract doesn't have, add it to the contract first (see claude-code-UI-prompt.md golden rule #1).
 */

/**
 * @typedef {Object} ProvisionDetails
 * @property {?number} provisionAmount GBP, 2dp (DFXX2)
 * @property {?string} provisionApprovalDate ISO date YYYY-MM-DD (DFXX3)
 * @property {?number} gceNetOfProvision GBP, 2dp (DFXX4)
 * @property {?number} grossLtvPct read-only, server-computed, % 0-100 2dp (DFXX5)
 * @property {?number} grossLtgdvPct read-only, server-computed (DFXX6)
 * @property {?number} netLtvPct read-only, server-computed (DFXX7)
 * @property {?number} netLtgdvPct read-only, server-computed (DFXX8)
 */

/**
 * @typedef {Object} ProvisionDetailsInput
 * @property {?number} provisionAmount
 * @property {?string} provisionApprovalDate ISO date YYYY-MM-DD
 * @property {?number} gceNetOfProvision
 */

/** @typedef {"FixChargeReceiver"|"Administrator"} FourthPartyRole */

/**
 * @typedef {Object} FourthParty
 * @property {FourthPartyRole} role (DFXX16)
 * @property {?string} companyId (DFXX17)
 * @property {?string} contactId (DFXX18)
 * @property {?string} contactNumber read from repository for an existing contact; captured for a new one (DFXX19)
 * @property {?string} [companyName] display-only, not itself a contract field — carried alongside companyId so the picker can render the chosen company without a round trip
 * @property {?string} [contactName] display-only, carried alongside contactId
 */

/**
 * @typedef {Object} Professionals
 * @property {?string} lawyerId (DFXX13)
 * @property {?string} valuerId (DFXX14)
 * @property {?string} surveyorId (DFXX15)
 * @property {?FourthParty} fourthParty absent/null hides Company/Contact/Contact Number (DFXX16-19)
 */

/**
 * @typedef {Object} Scenario
 * @property {string} id
 * @property {number} sequence 1-5, server-assigned label position
 * @property {?string} description
 * @property {?number} provisionPct 0.00-100.00, 2dp
 * @property {?number} weightingPct 0.00-100.00, 2dp
 */

/**
 * @typedef {Object} ScenarioInput
 * @property {?string} description
 * @property {?number} provisionPct
 * @property {?number} weightingPct
 */

/**
 * @typedef {Object} DcfDocument
 * @property {string} id
 * @property {string} fileName
 * @property {string} fileType display type e.g. PDF, Excel, Word
 * @property {string} filePath Document Helper storage reference — used to open/download directly
 * @property {string} fileExtension lower-case, leading dot, e.g. ".pdf"
 * @property {string} uploadedBy
 * @property {string} uploadedOn ISO date-time
 * @property {?number} sizeBytes client-reported, not server-verified
 */

/**
 * The POST /provision/dcf-documents body — a reference to a file ALREADY uploaded to the
 * Document Helper, not the file itself. Same shape as Professional Contacts' QuoteDocumentDto.
 * @typedef {Object} DcfDocumentInput
 * @property {string} fileName
 * @property {string} filePath
 * @property {string} fileExtension
 * @property {?string} contentType browser-reported MIME type; may be empty
 * @property {?number} sizeBytes
 */

/**
 * @typedef {Object} AuditStamp
 * @property {string} [createdBy]
 * @property {string} [createdOn] ISO date-time
 * @property {string} [lastModifiedBy]
 * @property {string} [lastModifiedOn] ISO date-time
 */

/**
 * @typedef {Object} Provision
 * @property {string} [loanId]
 * @property {boolean} editable true when loan CRR >= 6 — drives read-only state (DFXX1)
 * @property {ProvisionDetails} details
 * @property {Professionals} professionals
 * @property {Scenario[]} scenarios minItems 2, maxItems 5
 * @property {?number} provisionOutcomePct auto-calculated, read-only (DFXX10)
 * @property {?string} notes maxLength 2000 (DFXX12)
 * @property {DcfDocument[]} dcfDocuments
 * @property {AuditStamp} [audit]
 */

/**
 * The editable body of a Provision — PUT /loans/{loanId}/provision. Computed fields (ratios,
 * outcome) and audit are never sent back; scenarios and dcfDocuments have their own endpoints
 * and are not part of this payload either.
 * @typedef {Object} ProvisionUpsert
 * @property {ProvisionDetailsInput} details
 * @property {Professionals} professionals
 * @property {?string} notes
 */

/**
 * @typedef {Object} ProfessionalSummary
 * @property {string} id
 * @property {string} name
 * @property {?string} companyName
 */

/**
 * @typedef {Object} ProfessionalDetail
 * @property {string} id
 * @property {string} name
 * @property {?string} companyName
 * @property {?string} email
 * @property {?string} contactNumber
 */

/** @typedef {Object} CompanyInput
 * @property {string} name
 */

/**
 * @typedef {Object} ContactInput
 * @property {string} name
 * @property {?string} email
 * @property {?string} contactNumber
 */

/**
 * @typedef {ContactInput & {id: string, companyId: string, isMainContact?: boolean}} ContactDetail
 *   `isMainContact` is read-only (repository-owned): true for the company's single main/preferred
 *   contact, which the 4th-party Contact dropdown pre-selects. A contact created from this card is
 *   never main unless it is the company's first — see the contract's MAIN-CONTACT RULE.
 */

/**
 * @typedef {Object} ApiError
 * @property {string} code
 * @property {string} message
 * @property {?string} field offending field for validation errors
 */

/** The four professional picker types the repository search endpoint accepts (contract order). */
export const PROFESSIONAL_TYPES = ["lawyer", "valuer", "surveyor", "company"];

/** Roles for the optional 4th party, in contract order (DFXX16). */
export const FOURTH_PARTY_ROLES = ["FixChargeReceiver", "Administrator"];

/** Human labels for the 4th party role select. */
export const FOURTH_PARTY_ROLE_LABELS = {
  FixChargeReceiver: "Fix Charge Receiver",
  Administrator: "Administrator",
};

/**
 * Decimal places a £ amount may carry (DFXX2, DFXX4). The contract's money fields are 2dp, so
 * this is both what the inputs let the user type and what isValidMoney() checks on save.
 */
export const MONEY_DECIMAL_PLACES = 2;

/** Scenario count bounds enforced client-side (server also enforces via 409, DFXX9). */
export const MIN_SCENARIOS = 2;
export const MAX_SCENARIOS = 5;

/**
 * The Loan Documents category every DCF file is filed under, and the middle segment of its
 * Document Helper path. Fixed, never user-editable — the same arrangement as Professional
 * Contacts' QUOTE_DOCUMENT_CATEGORY_NAME ("Quotes"). Named by the backend as `dcf-documents`.
 */
export const DCF_DOCUMENT_CATEGORY_NAME = "dcf-documents";

/**
 * File types accepted for DCF upload (DFXX11) — matches the contract's
 * POST /provision/dcf-documents `x-constraints.allowedTypes` exactly.
 */
export const DCF_ALLOWED_CONTENT_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
];

/** File extensions accepted for DCF upload — client-side accept/validation mirrors DCF_ALLOWED_CONTENT_TYPES. */
export const DCF_ALLOWED_EXTENSIONS = [".pdf", ".xls", ".xlsx", ".doc", ".docx"];

/** Matches the contract's `x-constraints.maxSizeBytes` on POST /provision/dcf-documents (25 MB). */
export const DCF_MAX_SIZE_BYTES = 26214400;

export {};
